import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getData } from '../../../../services/apiService';
import { downloadFile } from '../../../Hooks/fileDownloader';
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    Calendar,
    Clock,
    Info,
    User,
    FileText,
    PartyPopper,
    GraduationCap,
    BookOpen,
    CreditCard,
    Receipt,
    Download,
    ArrowLeft,
    RefreshCw,
    Mail,
    Phone,
    Building,
    Hash
} from 'lucide-react';
import Swal from 'sweetalert2';

const PaymentStatus = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user.user);

    const [paymentData, setPaymentData] = useState(null);
    const [studentDetails, setStudentDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('loading');
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [transactionDate, setTransactionDate] = useState('');
    const [error, setError] = useState(null);

    const clearStoredData = () => {
        sessionStorage.removeItem('examPaymentData');
        sessionStorage.removeItem('ccavenuePaymentData');
        sessionStorage.removeItem('paymentDetails');
    };

    const BackToDashboard = () => {
        clearStoredData();
        navigate('/exam-fees');
    };

    const handleNewPayment = async (urlParams) => {
        const urlData = {};
        urlParams.forEach((value, key) => {
            urlData[key] = value;
        });

        const now = new Date();
        setTransactionDate(now.toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }));

        const studentData = {
            name: user?.name || urlData.name || 'Student',
            rollNo: user?.roll_no || urlData.rollNo || 'N/A',
            department: user?.department || user?.cour_LName || urlData.department || 'N/A',
            semester: user?.semNo ? `Semester ${user.semNo}` : urlData.semester || 'N/A',
            academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : urlData.academicYear || 'N/A',
            email: user?.email || urlData.email || 'student@example.com',
            phone: user?.phone || urlData.phone || 'N/A'
        };

        // Normalize the status value
        const rawStatus = urlData.STATUS || urlData.status || 'TXN_SUCCESS';
        const normalizedStatus = rawStatus.toLowerCase();

        let paymentStatus;
        if (normalizedStatus === 'success' || normalizedStatus === 'txn_success') {
            paymentStatus = 'TXN_SUCCESS';
        } else if (normalizedStatus === 'failure' || normalizedStatus === 'txn_failure') {
            paymentStatus = 'TXN_FAILURE';
        } else if (normalizedStatus === 'pending') {
            paymentStatus = 'PENDING';
        } else {
            paymentStatus = 'TXN_FAILURE';
        }

        const paymentData = {
            status: paymentStatus,
            amount: urlData.TXNAMOUNT || urlData.amount || 0,
            email: urlData.email || user?.email,
            phone: urlData.phone || user?.phone,
            txndate: urlData.txndate || urlData.TXNDATE || urlData.txnDate || now.toISOString(),
            timestamp: now.toISOString(),
            orderId: urlData.ORDERID || urlData.orderId,
            txnId: urlData.TXNID || urlData.txnId || `TXN${Date.now()}`,
            receiptNo: urlData.Receipt_No || urlData.receiptNo || `REC${Date.now()}`,
            paymentMode: urlData.paymentMode || 'Online'
        };

        setPaymentData(paymentData);
        setStudentDetails(studentData);

        const paymentStatusFinal = paymentData.status === 'TXN_SUCCESS' ? 'success' :
            paymentData.status === 'TXN_FAILURE' ? 'failed' :
                paymentData.status === 'PENDING' ? 'pending' : 'failed';
        setStatus(paymentStatusFinal);
    };

    const processPaymentData = async () => {
        try {
            setLoading(true);
            setError(null);

            const urlParams = new URLSearchParams(location.search);
            const orderId = urlParams.get('orderId');
            const gateway = urlParams.get('gateway');
            const statusParam = urlParams.get('status');
            const errorParam = urlParams.get('error');
            const txnId = urlParams.get('txnId');
            const amount = urlParams.get('amount');

            // Handle CCAvenue specifically
            if (gateway === 'ccavenue') {
                if (statusParam === 'success') {
                    const studentData = {
                        name: user?.name || 'Student',
                        rollNo: user?.roll_no || 'N/A',
                        department: user?.department || user?.cour_LName || 'N/A',
                        semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                        academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                        email: user?.email || 'student@example.com',
                        phone: user?.phone || 'N/A'
                    };

                    const paymentData = {
                        status: 'TXN_SUCCESS',
                        amount: amount || 0,
                        email: user?.email,
                        phone: user?.phone,
                        txndate: new Date().toISOString(),
                        timestamp: new Date().toISOString(),
                        orderId: orderId || 'N/A',
                        txnId: txnId || `TXN${Date.now()}`,
                        receiptNo: `REC${Date.now()}`,
                        paymentMode: 'CCAvenue'
                    };

                    setPaymentData(paymentData);
                    setStudentDetails(studentData);
                    setTransactionDate(new Date().toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }));
                    setStatus('success');
                    setLoading(false);
                    return;
                } else if (statusParam === 'cancelled') {
                    setStatus('cancelled');
                    setPaymentData({
                        status: 'CANCELLED',
                        amount: 0,
                        orderId: orderId || 'N/A',
                        txnId: 'N/A',
                        receiptNo: `REC${Date.now()}`,
                        paymentMode: 'CCAvenue'
                    });
                    setStudentDetails({
                        name: user?.name || 'Student',
                        rollNo: user?.roll_no || 'N/A',
                        department: user?.department || user?.cour_LName || 'N/A',
                        semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                        academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                        email: user?.email || 'student@example.com',
                        phone: user?.phone || 'N/A'
                    });
                    setTransactionDate(new Date().toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }));
                    setLoading(false);
                    return;
                } else if (statusParam === 'failure') {
                    setStatus('failed');
                    setError(errorParam || 'Payment failed');
                    setPaymentData({
                        status: 'TXN_FAILURE',
                        amount: 0,
                        orderId: orderId || 'N/A',
                        txnId: 'N/A',
                        receiptNo: `REC${Date.now()}`,
                        paymentMode: 'CCAvenue'
                    });
                    setStudentDetails({
                        name: user?.name || 'Student',
                        rollNo: user?.roll_no || 'N/A',
                        department: user?.department || user?.cour_LName || 'N/A',
                        semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                        academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                        email: user?.email || 'student@example.com',
                        phone: user?.phone || 'N/A'
                    });
                    setTransactionDate(new Date().toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }));
                    setLoading(false);
                    return;
                }
            }

            // Handle Easebuzz payment (regular GET request with parameters)
            if (urlParams.toString() && (statusParam || urlParams.get('STATUS'))) {
                const easebuzzStatus = statusParam || urlParams.get('STATUS');
                if (easebuzzStatus) {
                    await handleNewPayment(urlParams);
                    setLoading(false);
                    return;
                }
            }

            // Check stored data for other gateways
            const storedData = sessionStorage.getItem('examPaymentData');
            if (storedData) {
                try {
                    const parsedData = JSON.parse(storedData);
                    if (parsedData && parsedData.expires > Date.now()) {
                        setPaymentData(parsedData.payment);
                        setStudentDetails(parsedData.student);

                        const date = new Date(parsedData.payment.timestamp || parsedData.payment.txndate);
                        setTransactionDate(date.toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }));

                        const isSuccess = parsedData.payment.status === 'TXN_SUCCESS' ||
                            parsedData.payment.status === 'success';
                        setStatus(isSuccess ? 'success' : 'failed');
                        setLoading(false);
                        return;
                    }
                } catch (error) {
                    console.error('Error parsing stored data:', error);
                }
            }

            // If no data found, check if there's a status parameter
            if (statusParam || urlParams.get('STATUS')) {
                await handleNewPayment(urlParams);
            } else {
                setStatus('failed');
                setError('No payment data found');
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            setStatus('failed');
            setError(error.message || 'Failed to process payment');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        processPaymentData();
    }, [location]);

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        const num = parseFloat(amount);
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return transactionDate || 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const getStatusConfig = () => {
        const configs = {
            success: {
                icon: <CheckCircle className="w-12 h-12" />,
                title: 'Payment Successful',
                message: 'Your exam fee has been paid successfully.',
                headerBg: 'bg-gradient-to-r from-emerald-500 to-green-600',
                bodyBg: 'bg-gradient-to-b from-emerald-50 via-green-50 to-emerald-25',
                iconColor: 'text-emerald-600',
                textColor: 'text-emerald-700',
                borderColor: 'border-emerald-200',
                buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
                lightColor: 'bg-emerald-50',
                mediumColor: 'bg-emerald-100',
                darkColor: 'bg-emerald-600',
                greetingIcon: <PartyPopper className="w-8 h-8" />
            },
            failed: {
                icon: <XCircle className="w-12 h-12" />,
                title: 'Payment Failed',
                message: 'Transaction could not be completed.',
                headerBg: 'bg-gradient-to-r from-rose-500 to-red-600',
                bodyBg: 'bg-gradient-to-b from-rose-50 via-red-50 to-rose-25',
                iconColor: 'text-rose-600',
                textColor: 'text-rose-700',
                borderColor: 'border-rose-200',
                buttonColor: 'bg-rose-600 hover:bg-rose-700',
                lightColor: 'bg-rose-50',
                mediumColor: 'bg-rose-100',
                darkColor: 'bg-rose-600'
            },
            pending: {
                icon: <Clock className="w-12 h-12" />,
                title: 'Payment Pending',
                message: 'Your payment is being processed.',
                headerBg: 'bg-gradient-to-r from-amber-500 to-orange-600',
                bodyBg: 'bg-gradient-to-b from-amber-50 via-orange-50 to-amber-25',
                iconColor: 'text-amber-600',
                textColor: 'text-amber-700',
                borderColor: 'border-amber-200',
                buttonColor: 'bg-amber-600 hover:bg-amber-700',
                lightColor: 'bg-amber-50',
                mediumColor: 'bg-amber-100',
                darkColor: 'bg-amber-600'
            },
            cancelled: {
                icon: <AlertCircle className="w-12 h-12" />,
                title: 'Payment Cancelled',
                message: 'You cancelled the payment process.',
                headerBg: 'bg-gradient-to-r from-gray-500 to-gray-600',
                bodyBg: 'bg-gradient-to-b from-gray-50 via-gray-50 to-gray-25',
                iconColor: 'text-gray-600',
                textColor: 'text-gray-700',
                borderColor: 'border-gray-200',
                buttonColor: 'bg-gray-600 hover:bg-gray-700',
                lightColor: 'bg-gray-50',
                mediumColor: 'bg-gray-100',
                darkColor: 'bg-gray-600'
            },
            default: {
                icon: <AlertCircle className="w-12 h-12" />,
                title: 'Payment Status',
                message: 'Processing payment status...',
                headerBg: 'bg-gradient-to-r from-gray-500 to-gray-600',
                bodyBg: 'bg-gradient-to-b from-gray-50 via-gray-50 to-gray-25',
                iconColor: 'text-gray-600',
                textColor: 'text-gray-700',
                borderColor: 'border-gray-200',
                buttonColor: 'bg-gray-600 hover:bg-gray-700',
                lightColor: 'bg-gray-50',
                mediumColor: 'bg-gray-100',
                darkColor: 'bg-gray-600'
            }
        };

        return configs[status] || configs.default;
    };

    const config = getStatusConfig();

    const handleDownloadReceipt = async () => {
        if (!paymentData?.orderId) {
            Swal.fire({
                title: 'Error',
                text: 'Order ID not available. Please refresh the page and try again.',
                icon: 'error',
                confirmButtonColor: config.darkColor,
            });
            return;
        }

        setIsApiLoading(true);

        const result = await downloadFile({
            getData,
            url: `/payment/payment-receipt?order_id=${encodeURIComponent(paymentData.orderId)}&format=`,
            fileType: 'pdf',
            defaultFilename: `Payment_Receipt_${paymentData.orderId}`,
        });

        setIsApiLoading(false);

        // Optional: if you still want the extra Swal success popup on top of the toast,
        // uncomment the block below. Otherwise remove this entire if-block.
        // if (result.success) {
        //     Swal.fire({
        //         title: 'Success!',
        //         text: 'Receipt downloaded successfully',
        //         icon: 'success',
        //         confirmButtonColor: config.darkColor,
        //         timer: 2000
        //     });
        // }
    };

    const handleRetryPayment = () => {
        clearStoredData();
        navigate('/exam-fees');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Verifying payment...</p>
                </div>
            </div>
        );
    }

    if (error && !paymentData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-12 h-12 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Error</h3>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={handleRetryPayment}
                        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-blue-600 hover:to-indigo-700 transition duration-200"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${config.bodyBg} py-8 px-4 transition-colors duration-300`}>
            <div className="max-w-5xl mx-auto border-2 border-gray-200 rounded-xl bg-white shadow-2xl overflow-hidden">
                {/* Header Status Card */}
                <div className={`shadow-xl ${config.headerBg} text-white p-8 transform transition-all duration-300 hover:shadow-2xl`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div className="flex items-center space-x-6 mb-6 md:mb-0">
                            <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
                                {React.cloneElement(config.icon, { className: "w-16 h-16 text-white" })}
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold mb-2">{config.title}</h1>
                                <p className="text-white/90 text-lg">{config.message}</p>
                                {status === 'success' && (
                                    <div className="flex items-center mt-3">
                                        <Mail className="w-5 h-5 mr-2" />
                                        <span className="text-sm">Receipt sent to your registered email</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-4xl md:text-5xl font-bold mb-2">{formatCurrency(paymentData?.amount)}</div>
                            <p className="text-white/80">
                                {formatDate(paymentData?.txndate || paymentData?.timestamp || Date.now())}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Two Column Details */}
                <div className="grid lg:grid-cols-2 gap-1 mb-1">
                    {/* Left Column: Student Details */}
                    <div className={`bg-white shadow-lg border ${config.borderColor} p-6 transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-base font-bold ${config.textColor} flex items-center`}>
                                <GraduationCap className={`w-6 h-6 mr-3 ${config.iconColor}`} />
                                Student Details
                            </h3>
                            <div className={`px-3 py-1 ${config.lightColor} rounded-full`}>
                                <span className={`text-sm font-medium ${config.textColor}`}>
                                    {status === 'success' ? '✓ Verified' : status.toUpperCase()}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <User className="w-4 h-4 mr-2 text-gray-400" />
                                        Student Name
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-semibold text-gray-800 text-xl">
                                        {studentDetails?.name || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Hash className="w-4 h-4 mr-2 text-gray-400" />
                                        Roll Number
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-mono font-semibold text-gray-800 text-lg">
                                        {studentDetails?.rollNo || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Building className="w-4 h-4 mr-2 text-gray-400" />
                                        Department
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {studentDetails?.department || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <BookOpen className="w-4 h-4 mr-2 text-gray-400" />
                                        Semester
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {studentDetails?.semester || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                        Academic Year
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {studentDetails?.academicYear || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Transaction Details */}
                    <div className={`bg-white shadow-lg border ${config.borderColor} p-6 transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-base font-bold ${config.textColor} flex items-center`}>
                                <CreditCard className={`w-6 h-6 mr-3 ${config.iconColor}`} />
                                Transaction Details
                            </h3>
                            <div className={`px-3 py-1 ${config.lightColor} rounded-full`}>
                                <span className={`text-sm font-medium ${config.textColor}`}>
                                    {paymentData?.paymentMode || 'Online'}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {paymentData?.receiptNo && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <Receipt className="w-4 h-4 mr-2 text-gray-400" />
                                            Receipt Number
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className={`font-mono font-semibold text-lg ${config.textColor} ${config.lightColor} px-4 py-2 rounded-lg block`}>
                                            {paymentData?.receiptNo}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {paymentData?.txnId && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <FileText className="w-4 h-4 mr-2 text-gray-400" />
                                            Transaction ID
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className="font-mono font-semibold text-gray-800 text-lg">
                                            {paymentData?.txnId}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {paymentData?.orderId && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <Hash className="w-4 h-4 mr-2 text-gray-400" />
                                            Order ID
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className="font-mono font-semibold text-gray-800 text-lg">
                                            {paymentData?.orderId}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                                        Transaction Date & Time
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {formatDate(paymentData?.txndate || paymentData?.timestamp)}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                                <div>
                                    <div className="flex items-center mb-1">
                                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-xs text-gray-500">Email</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-800 break-all">
                                        {studentDetails?.email || paymentData?.email || 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <div className="flex items-center mb-1">
                                        <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-xs text-gray-500">Phone</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-800">
                                        {studentDetails?.phone || paymentData?.phone || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Section - Success or Failure Messages */}
                {status === 'success' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex flex-col items-center text-center">
                            <div className={`mb-6 p-4 rounded-full ${config.mediumColor}`}>
                                {React.cloneElement(config.greetingIcon, { className: `w-12 h-12 ${config.iconColor}` })}
                            </div>
                            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${config.textColor}`}>
                                Exam Fee Payment Completed Successfully! 🎉
                            </h2>
                            <div className={`max-w-2xl mx-auto mb-8 p-4 ${config.mediumColor} rounded-lg`}>
                                <p className="text-lg font-medium text-gray-700">
                                    Your exam fee has been paid successfully.
                                </p>
                                <p className={`text-xl font-bold mt-2 ${config.textColor}`}>
                                    Hall Ticket will be available soon.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                                <button
                                    onClick={handleDownloadReceipt}
                                    disabled={isApiLoading}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl 
                                    bg-gradient-to-r from-emerald-500 to-teal-600 
                                    text-white font-semibold shadow-lg 
                                    hover:from-emerald-600 hover:to-teal-700 
                                    hover:shadow-xl active:scale-95 
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-all duration-200"
                                >
                                    {isApiLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-5 h-5" />
                                            Download Receipt
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={BackToDashboard}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                                    bg-white border-2 border-emerald-200 text-emerald-700 font-semibold
                                    hover:bg-emerald-50 hover:border-emerald-300
                                    transition-all duration-200"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                    Back to Fees
                                </button>
                            </div>
                        </div>
                    </div>
                ) : status === 'failed' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-start">
                            <div className={`p-3 rounded-full ${config.mediumColor} mr-4 flex-shrink-0`}>
                                <Info className={`w-6 h-6 ${config.iconColor}`} />
                            </div>
                            <div className="flex-1">
                                <h4 className={`text-xl font-bold mb-4 ${config.textColor}`}>
                                    📋 Important Information
                                </h4>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">You can retry the payment from your dashboard</p>
                                        </div>
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">No charges will be applied for failed transactions</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Contact support if amount was deducted but failed</p>
                                        </div>
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Check your bank statement for any deductions</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                            <button
                                onClick={BackToDashboard}
                                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                                bg-white border-2 border-gray-300 text-gray-700 font-semibold
                                hover:bg-gray-50 hover:border-gray-400
                                transition-all duration-200"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Back to Fees
                            </button>
                            <button
                                onClick={handleRetryPayment}
                                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-rose-500 to-red-600
                                text-white font-semibold shadow-lg
                                hover:from-rose-600 hover:to-red-700
                                transition-all duration-200"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Retry Payment
                            </button>
                        </div>
                    </div>
                ) : status === 'cancelled' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor}`}>
                        <div className="flex flex-col items-center text-center">
                            <div className={`p-4 rounded-full ${config.mediumColor} mb-4`}>
                                <AlertCircle className={`w-12 h-12 ${config.iconColor}`} />
                            </div>
                            <h2 className={`text-2xl font-bold mb-2 ${config.textColor}`}>
                                Payment Cancelled
                            </h2>
                            <p className="text-gray-600 mb-6">You have cancelled the payment process.</p>
                            <button
                                onClick={handleRetryPayment}
                                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-blue-500 to-indigo-600
                                text-white font-semibold shadow-lg
                                hover:from-blue-600 hover:to-indigo-700
                                transition-all duration-200"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Try Again
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor}`}>
                        <div className="flex flex-col items-center text-center">
                            <div className={`p-4 rounded-full ${config.mediumColor} mb-4`}>
                                <Clock className={`w-12 h-12 ${config.iconColor}`} />
                            </div>
                            <h2 className={`text-2xl font-bold mb-2 ${config.textColor}`}>
                                Payment Pending
                            </h2>
                            <p className="text-gray-600 mb-6">Your payment is being processed. Please wait.</p>
                            <button
                                onClick={BackToDashboard}
                                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-amber-500 to-orange-600
                                text-white font-semibold shadow-lg
                                hover:from-amber-600 hover:to-orange-700
                                transition-all duration-200"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentStatus;